import React, { useState, useEffect } from 'react';
import { Layout, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import Sidebar from '../components/userHomeSideBar/sideBar';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/DBContext';

const { Content } = Layout;

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showBanner, setShowBanner] = useState(false);
  const [userRole, setUserRole] = useState(null); // State to store user role
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 576);
  const location = useLocation();

  const { getUserInfo, currentUser } = useAuth(); // Access the function from DBContext

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 576);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch user info to determine role and banner visibility
  useEffect(() => {
    setLoading(true);
    const fetchUserRole = async () => {
      const userInfo = await getUserInfo(currentUser.uid);
      const role = userInfo?.role;
      // console.log('banner' + userInfo.role);
      setUserRole(role);
      setShowBanner(['family', 'business', 'local-authority'].includes(role));
      setLoading(false); // Move setLoading(false) here
    };

    fetchUserRole();
  }, [getUserInfo, currentUser.uid]);

  const getBannerContent = () => {
    // Define banner content per role and route
    const roleBannerContent = {
      family: {
        '/dashboard': { header: 'My enableSEND', text: 'Manage your bookings, see your favourite services and products, update your profile, your child/ren’s profile and manage your subscription. ' },
        '/map': { header: 'My SEND Map', text: 'Find services available across the UK that are SEND accessible. Don’t forget to click on the service you like and book. ' },
        '/blog': { header: 'SEND News', text: 'Keep up to date with the latest news and thoughts from our SEND community via blog posts and articles. Use our comments function to join in the discussion with other families and providers. For specific resources check out My SEND resources page.' },
        '/products': { header: 'My SEND products', text: 'Look through our list of recommended products to support SEND needs, understand what products support different areas of need, where to buy and how to use them. SEND specific and targeted products endorsed by us for you to use. Check out the reviews to see what the community thinks. ' },
        '/resources': { header: 'My SEND Resources', text: 'Save, download and share resources, information, advice and guidance from our library of SEND resources. Get to grips with SEND legislation, download visual, communication and support tools, or learn about SEND processes. ' },
        '/service': { header: 'SEND News', text: 'Finding the map overwhelming? Search our list format of services available across the UK that are SEND accessible. Don’t forget to click on the service you like to see more information and book. You can also contact them through the messaging system.' },

      },
      business: {
        '/business-profile': { header: 'My enableSEND', text: 'Update your profile, view your favourite services and products, and see your claimed services' },
        '/business-service': { header: 'My enableSEND', text: 'Manage your services, update details on the services you offer, and create and manage bookings' },
        '/map': { header: 'My SEND Map', text: 'Find services in your local area, promote your services and share useful services with families.' },
        '/blog': { header: 'SEND News', text: 'Keep up to date with the latest news and thoughts from our SEND community via blog posts and articles. Use our comments function to join in the discussion and get valuable insight from users. For specific resources check out My SEND resources page.' },
        '/resources': { header: 'My SEND Resources', text: 'Save, download and share resources, information, advice and guidance from our library of SEND resources. Use these within your setting to support best practice.' },
        '/products': { header: 'My SEND products', text: 'Understand what products support different needs, where to buy and how to use them. SEND specific and targeted products endorsed by us for you to use within your setting.' },
        '/service': { header: 'SEND News', text: 'Finding the map overwhelming? Search our list format of services available across the UK that are SEND accessible. Find services in your local area, promote your services and share useful services with families.' },

      },
      'local-authority': {
        '/la-users': { header: 'My enableSEND', text: 'Manage your services, oversee users, and update your account details' },
        '/la-business': { header: 'My enableSEND', text: 'Manage your services, oversee users, and update your account details' },
        '/map': { header: 'My SEND Map', text: ' Find services available in your local authority area to contact, build relationships with and share with your families' },
        '/blog': { header: 'SEND News', text: 'Keep up to date with the latest news and thoughts from our SEND community via blog posts and articles. Use our comments function to join in the discussion and get valuable insight from users. For specific resources check out My SEND resources page. ' },
        '/resources': { header: 'My SEND Resources', text: ': Save, download and share resources, information, advice and guidance from our library of SEND resources. Use these within your teams to support best practice. ' },
        '/products': { header: 'My SEND products', text: ' Understand what products support different needs, where to buy and how to use them. SEND specific and targeted products endorsed by us for you to use. Check out the reviews to see what the community thinks. ' },
        '/service': { header: 'SEND News', text: 'Finding the map overwhelming? Search our list format of services available across the UK that are SEND accessible. Promote best practice in your local authority area by connecting, building relationships, and sharing with your families.' },

      },
    };

    // Default content if the route or role doesn't match
    return roleBannerContent[userRole]?.[location.pathname] || { header: null, text: null };
  };

  const { header: bannerHeader, text: bannerText } = getBannerContent();

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar collapsed={collapsed} onCollapse={setCollapsed} />

      <Layout style={{ 
        marginLeft: collapsed ? 0 : (isMobile ? 0 : 220), 
        transition: 'margin-left 0.2s',
        display: isMobile && !collapsed ? 'none' : 'block' 
      }}>
        {/* Conditionally render the banner only if bannerText is not null */}
        { bannerText && (
         <div className="relative isolate flex items-center gap-x-6 overflow-hidden bg-gray-50 px-6 py-2.5 sm:px-3.5 sm:before:flex-1">
         <div
           aria-hidden="true"
           className="absolute left-[max(-7rem,calc(50%-52rem))] top-1/2 -z-10 -translate-y-1/2 transform-gpu blur-2xl"
         >
           <div
             style={{
               clipPath:
                 'polygon(74.8% 41.9%, 97.2% 73.2%, 100% 34.9%, 92.5% 0.4%, 87.5% 0%, 75% 28.6%, 58.5% 54.6%, 50.1% 56.8%, 46.9% 44%, 48.3% 17.4%, 24.7% 53.9%, 0% 27.9%, 11.9% 74.2%, 24.9% 54.1%, 68.6% 100%, 74.8% 41.9%)',
             }}
             className="aspect-[577/310] w-[36.0625rem] bg-gradient-to-r from-[#ff80b5] to-[#9089fc] opacity-30"
           />
         </div>
         <div
           aria-hidden="true"
           className="absolute left-[max(45rem,calc(50%+8rem))] top-1/2 -z-10 -translate-y-1/2 transform-gpu blur-2xl"
         >
           <div
             style={{
               clipPath:
                 'polygon(74.8% 41.9%, 97.2% 73.2%, 100% 34.9%, 92.5% 0.4%, 87.5% 0%, 75% 28.6%, 58.5% 54.6%, 50.1% 56.8%, 46.9% 44%, 48.3% 17.4%, 24.7% 53.9%, 0% 27.9%, 11.9% 74.2%, 24.9% 54.1%, 68.6% 100%, 74.8% 41.9%)',
             }}
             className="aspect-[577/310] w-[36.0625rem] bg-gradient-to-r from-[#ff80b5] to-[#9089fc] opacity-30"
           />
         </div>
         <p className="text-sm/6 text-gray-900">
           <a href="#">
             {/* <strong className="font-semibold">{bannerHeader}</strong> */}
             {/* <svg viewBox="0 0 2 2" aria-hidden="true" className="mx-2 inline h-0.5 w-0.5 fill-current">
               <circle r={1} cx={1} cy={1} />
             </svg> */}
             {bannerText}
           </a>
         </p>
         <div className="flex flex-1 justify-end">
         
         </div>
       </div>
        )}

        <Content style={{ margin: '24px 16px 0', overflow: 'initial' }}>
          <Outlet />
        </Content>
      </Layout>

      {loading && (
        <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          backgroundColor: 'white',
        }}
      >
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
      </div>
      )}
    </Layout>
  );
};

export default MainLayout;
